import { useRef, useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth/AuthContext'
import { signup } from '@/features/auth/api'
import { AuthInfoPanel } from '@/features/auth/AuthInfoPanel'
import { PasswordField } from '@/features/auth/PasswordField'
import { Recaptcha, type RecaptchaHandle } from '@/features/auth/Recaptcha'
import { ApiError } from '@/api/client'
import logo from '@/assets/vision_controller_logo.jpg'

/**
 * Ports components/user/user_register.html - same two-column layout (form
 * card + "Vision Portal" info card) and self-service client signup
 * behavior (new user always created with is_client=True, no company
 * assigned, matching the legacy form's defaults exactly).
 *
 * SignUpForm's RecaptchaField was originally not ported here on the
 * mistaken belief that recaptcha was never configured - since corrected
 * for LoginPage.tsx (see Recaptcha.tsx), and restored here too: this was
 * the one public, unauthenticated form left with no bot-abuse protection
 * at all (backend/.../auth.py's api_signup docstring documented this as a
 * still-open gap).
 */
export default function SignupPage() {
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [firstname, setFirstname] = useState('')
  const [lastname, setLastname] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [password1, setPassword1] = useState('')
  const [password2, setPassword2] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const recaptchaRef = useRef<RecaptchaHandle>(null)

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (password1.length < 10) {
      setError('Password must be at least 10 characters')
      return
    }
    if (password1 !== password2) {
      setError('Passwords do not match')
      return
    }
    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA challenge.')
      return
    }
    setSubmitting(true)
    try {
      await signup({ firstname, lastname, email, password: password1, phone, recaptchaToken })
      navigate('/login', { replace: true, state: { justSignedUp: true } })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again later.')
      // Single-use and expires - clear it so the next submit can't
      // silently reuse a stale token (same pattern as LoginPage).
      recaptchaRef.current?.reset()
      setRecaptchaToken(null)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-secondary px-4 py-12">
      <div className="grid w-full max-w-3xl grid-cols-1 gap-6 rounded-lg border border-border bg-card p-6 shadow-sm md:grid-cols-2 md:p-8">
        <div>
          <div className="mb-1 flex items-center gap-2">
            <img src={logo} alt="Vision Portal" className="h-8 w-8 object-contain" />
            <h1 className="text-2xl font-bold text-brand">Vision Portal</h1>
          </div>
          <h2 className="mb-6 text-xl font-light text-text-primary">Signup</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex gap-3">
              <div className="flex-1">
                <label htmlFor="firstname" className="mb-1 block text-sm text-text-primary">
                  First name
                </label>
                <input
                  id="firstname"
                  required
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value)}
                  className="w-full rounded border-2 border-brand bg-input px-3 py-2 text-text-primary outline-none"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="lastname" className="mb-1 block text-sm text-text-primary">
                  Last name
                </label>
                <input
                  id="lastname"
                  required
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                  className="w-full rounded border-2 border-brand bg-input px-3 py-2 text-text-primary outline-none"
                />
              </div>
            </div>
            <div>
              <label htmlFor="email" className="mb-1 block text-sm text-text-primary">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded border-2 border-brand bg-input px-3 py-2 text-text-primary outline-none"
              />
            </div>
            <div>
              <label htmlFor="phone" className="mb-1 block text-sm text-text-primary">
                Phone
              </label>
              <input
                id="phone"
                type="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full rounded border-2 border-brand bg-input px-3 py-2 text-text-primary outline-none"
              />
            </div>
            <PasswordField
              id="password1"
              label="Password"
              autoComplete="new-password"
              value={password1}
              onChange={(e) => setPassword1(e.target.value)}
            />
            <PasswordField
              id="password2"
              label="Confirm password"
              autoComplete="new-password"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
            />

            <Recaptcha ref={recaptchaRef} onChange={setRecaptchaToken} />

            {error && <p className="text-sm italic text-error">{error}</p>}

            <button
              type="submit"
              disabled={submitting || !recaptchaToken}
              className="w-full rounded bg-brand py-2 font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? 'Creating account…' : 'Create account'}
            </button>

            <p className="text-center text-sm text-text-secondary">
              Already have an account?{' '}
              <Link to="/login" className="text-brand hover:underline">
                Sign in
              </Link>
            </p>
          </form>
        </div>

        <AuthInfoPanel />
      </div>
    </div>
  )
}
